import { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { DATE_FORMAT, ReplaceUrlWithLink } from 'framework/utils';
import { Collapsible, HtmlField } from 'framework/ui/field';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import { ShowDate } from 'framework/ui/ui-builder';
import { SummaryReviewDetails } from '../types';
import ControlPlaneAccordion from '../result/components/ControlPlaneAccordion';
import StaleItemsList from '../result/components/StaleItemsList';
import groupSummaryReviewItems, { getStaleSummaryReviewViolations } from './groupSummaryReviewItems';

interface SummaryReviewResultProps {
  review: SummaryReviewDetails;
}

const SummaryReviewResult = ({ review }: SummaryReviewResultProps) => {
  const sections = useMemo(() => groupSummaryReviewItems(review.items), [review.items]);
  const staleViolations = useMemo(() => getStaleSummaryReviewViolations(review.items), [review.items]);

  const comments = useMemo(
    () => Object.fromEntries(review.items.map(item => [item.entityId, item.comment ?? ''])),
    [review.items],
  );

  const openComments = useMemo(
    () => new Set(review.items.filter(item => item.comment).map(item => item.entityId)),
    [review.items],
  );

  return (
    <Box sx={{ mt: -3 }}>
      <Typography variant="body1" color="textSecondary" sx={{ pb: 0.5 }}>
        <ShowDate dateUTC={review.startDate} format={DATE_FORMAT.API_ONLY_DATE} />
        {' – '}
        <ShowDate dateUTC={review.endDate} format={DATE_FORMAT.API_ONLY_DATE} />
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ pb: 4 }}>
        Reviewer: <EmployeeLinkFieldWithNameLoading value={review.createdBy} />
      </Typography>

      <StaleItemsList staleItems={staleViolations} />

      {sections.length ? (
        sections.map((section, index) => (
          <ControlPlaneAccordion
            key={section.section}
            comments={comments}
            defaultExpanded={index === 0}
            isCommentingDisabled
            openComments={openComments}
            section={section}
          />
        ))
      ) : !staleViolations.length ? (
        <Typography color="text.secondary">No commented items in this review.</Typography>
      ) : null}

      {review.noteText && (
        <Collapsible>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="body1" color="textSecondary">
              Finalization note:
            </Typography>
            <ReplaceUrlWithLink>
              <HtmlField source="noteText" label="" />
            </ReplaceUrlWithLink>
          </Stack>
        </Collapsible>
      )}
    </Box>
  );
};

export default SummaryReviewResult;
